import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Flex,
  Text,
  Input,
  Box,
  VStack,
  HStack,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { getMissions } from 'api/api';
import apiClient from 'api/client';
import dayjs from 'dayjs';

export default function LinkCallToMissionModal({ isOpen, onClose, uniqueid, callerId, isCallActive = false, onLinkSuccess }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [missions, setMissions] = useState([]);
  const [filteredMissions, setFilteredMissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedMission, setSelectedMission] = useState(null);
  const [showMissionList, setShowMissionList] = useState(false);
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const cancelRef = React.useRef();

  // Réinitialiser l'état quand le modal s'ouvre/ferme
  useEffect(() => {
    if (isOpen) {
      setShowMissionList(false);
      setSearchQuery('');
      setSelectedMission(null);
    }
  }, [isOpen]);

  // Filtrer les missions selon la recherche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredMissions(missions);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = missions.filter(mission => {
      const id = String(mission.id || '').toLowerCase();
      const patientName = (mission.patient?.nom || mission.patient?.prenom || mission.patient_name || '').toLowerCase();
      const date = String(mission.date_mission || '').toLowerCase();
      const address = String(mission.adresse || '').toLowerCase();
      const addressDest = String(mission.adresse_destination || '').toLowerCase();
      const docA = String(mission.doc_a || '').toLowerCase();
      const docO = String(mission.doc_on || '').toLowerCase();

      return (
        id.includes(query) ||
        patientName.includes(query) ||
        date.includes(query) ||
        address.includes(query) ||
        addressDest.includes(query) ||
        docA.includes(query) ||
        docO.includes(query)
      );
    });

    setFilteredMissions(filtered);
  }, [searchQuery, missions]);

  const loadMissions = async () => {
    setLoading(true);
    try {
      const response = await getMissions();
      const missionsData = Array.isArray(response.data) ? response.data : (response.data?.missions || []);
      setMissions(missionsData);
      setFilteredMissions(missionsData);
    } catch (error) {
      console.error('Erreur chargement missions:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les missions',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMission = (mission) => {
    setSelectedMission(mission);
    onConfirmOpen();
  };

  const handleConfirmLink = async () => {
    if (!selectedMission || !uniqueid) {
      toast({
        title: 'Erreur',
        description: 'Données manquantes pour lier l\'appel',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Construire le chemin audio
      let audioPath = null;
      
      // Si l'appel est encore en cours, construire directement le chemin
      if (isCallActive && uniqueid) {
        // Format: /uploads/DD-MM-YYYY/uniqueid.webm
        const today = dayjs();
        const dateStr = today.format('DD-MM-YYYY');
        audioPath = `/uploads/${dateStr}/${uniqueid}.webm`;
      } else {
        // Sinon, récupérer depuis localStorage (appel terminé)
        try {
          const audioInfoStr = localStorage.getItem(`call_audio_${uniqueid}`);
          if (audioInfoStr) {
            const audioInfo = JSON.parse(audioInfoStr);
            audioPath = audioInfo.audioPath;
          }
        } catch (e) {
          console.warn('Impossible de récupérer le chemin audio:', e);
        }
      }

      const linkPayload = {
        missionid: selectedMission.id,
        callid: uniqueid, // Utiliser l'uniqueid directement comme callid
        lien: audioPath, // Chemin du fichier audio
      };
      
      const response = await apiClient.post('/missioncalls', linkPayload);

      const result = response.data;

      if (result.success || response.status === 200 || response.status === 201) {
        toast({
          title: 'Appel lié',
          description: `L'appel a été lié à la mission #${selectedMission.id}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onConfirmClose();
        onClose();
        if (onLinkSuccess) {
          onLinkSuccess(selectedMission.id);
        }
      } else {
        throw new Error(result.error || result.message || 'Erreur lors de la liaison');
      }
    } catch (error) {
      console.error('Erreur liaison appel-mission:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Impossible de lier l\'appel à la mission';
      toast({
        title: 'Erreur',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCreateNewMission = () => {
    // Sauvegarder l'uniqueid dans localStorage pour utilisation ultérieure
    if (uniqueid) {
      try {
        // Construire le chemin audio
        let audioPath = null;
        
        // Si l'appel est encore en cours, construire directement le chemin
        if (isCallActive && uniqueid) {
          // Format: /uploads/DD-MM-YYYY/uniqueid.webm
          const today = dayjs();
          const dateStr = today.format('DD-MM-YYYY');
          audioPath = `/uploads/${dateStr}/${uniqueid}.webm`;
        } else {
          // Sinon, récupérer depuis localStorage (appel terminé)
          try {
            const audioInfoStr = localStorage.getItem(`call_audio_${uniqueid}`);
            if (audioInfoStr) {
              const audioInfo = JSON.parse(audioInfoStr);
              audioPath = audioInfo.audioPath;
            }
          } catch (e) {
            console.warn('Impossible de récupérer le chemin audio:', e);
          }
        }

        const pendingCall = {
          uniqueid: uniqueid,
          callerId: callerId,
          audioPath: audioPath, // Inclure le chemin audio
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem('pending_call_for_mission', JSON.stringify(pendingCall));
      } catch (e) {
        console.error('Erreur sauvegarde appel:', e);
      }
    }

    onClose();
    navigate('/admin/missions/create');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return dayjs(dateString).format('DD/MM/YYYY HH:mm');
    } catch {
      return dateString;
    }
  };

  const getPatientName = (mission) => {
    if (mission.patient) {
      const nom = mission.patient.nom || '';
      const prenom = mission.patient.prenom || '';
      return `${prenom} ${nom}`.trim() || 'N/A';
    }
    return mission.patient_name || 'N/A';
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Lier l'appel à une mission</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Appel entrant de <strong>{callerId || 'Inconnu'}</strong>
              </Text>

              {!showMissionList ? (
                <Flex gap={2}>
                  <Button
                    flex={1}
                    colorScheme="blue"
                    onClick={() => {
                      setShowMissionList(true);
                      loadMissions();
                    }}
                  >
                    Lier à une mission existante
                  </Button>
                  <Button
                    flex={1}
                    colorScheme="green"
                    onClick={handleCreateNewMission}
                  >
                    Créer une nouvelle mission
                  </Button>
                </Flex>
              ) : (
                <>
                  <Button
                    mb={4}
                    size="sm"
                    variant="outline"
                    onClick={() => setShowMissionList(false)}
                  >
                    ← Retour
                  </Button>
                  {/* Liste des missions avec recherche */}
                  <Box>
                <Input
                  placeholder="Rechercher par ID, nom patient, date, adresse..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  mb={4}
                />

                <Box
                  maxH="400px"
                  overflowY="auto"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={2}
                >
                  {loading ? (
                    <Text textAlign="center" py={4}>Chargement...</Text>
                  ) : filteredMissions.length === 0 ? (
                    <Text textAlign="center" py={4} color="gray.500">
                      {searchQuery ? 'Aucune mission trouvée' : 'Aucune mission disponible'}
                    </Text>
                  ) : (
                    <VStack spacing={2} align="stretch">
                      {filteredMissions.map((mission) => (
                        <Box
                          key={mission.id}
                          p={3}
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          cursor="pointer"
                          _hover={{ bg: "gray.50", borderColor: "blue.300" }}
                          onClick={() => handleSelectMission(mission)}
                        >
                          <HStack justify="space-between" mb={2}>
                            <Text fontWeight="bold" fontSize="sm">
                              Mission #{mission.id}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {formatDate(mission.date_mission)}
                            </Text>
                          </HStack>
                          <Text fontSize="sm" mb={1}>
                            <strong>Patient:</strong> {getPatientName(mission)}
                          </Text>
                          {mission.adresse && (
                            <Text fontSize="xs" color="gray.600" mb={1}>
                              📍 {mission.adresse}
                            </Text>
                          )}
                          {mission.adresse_destination && (
                            <Text fontSize="xs" color="gray.600" mb={1}>
                              🎯 Destination: {mission.adresse_destination}
                            </Text>
                          )}
                          <HStack spacing={4} fontSize="xs" color="gray.500">
                            {mission.doc_a && <Text>Doc A: {mission.doc_a}</Text>}
                            {mission.doc_on && <Text>Doc ON: {mission.doc_on}</Text>}
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>
              </Box>
                </>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Dialog de confirmation */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmer la liaison
            </AlertDialogHeader>
            <AlertDialogBody>
              Êtes-vous sûr de vouloir lier cet appel à la mission #{selectedMission?.id} ?
              <br />
              <Text fontSize="sm" color="gray.600" mt={2}>
                Patient: {selectedMission ? getPatientName(selectedMission) : 'N/A'}
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>
                Annuler
              </Button>
              <Button colorScheme="blue" onClick={handleConfirmLink} ml={3}>
                Confirmer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

